import { useDroppable } from '@dnd-kit/core';
import { Box, createStyles } from '@mantine/core';
import { useHover, useMergedRef } from '@mantine/hooks';
import {
	INDICATOR_SIZE,
	ITEM_GAP,
	VERTICAL_INDICATOR_AFTER_ID_PREFIX,
	VERTICAL_INDICATOR_BEFORE_ID_PREFIX,
} from './constants';

interface DnDVerticalIndicatorProps {
	id: string;
	isFirstItem: boolean;
	isLastItem: boolean;
	isHoveringOverSameRow: boolean;
	disabled: boolean;
	readOnly?: boolean;
	onResizeStart?: (indicatorId: string, mouseX: number) => void;
}

interface DnDVerticalIndicatorStylesProps {
	isFirstIndicator: boolean;
	isLastIndicator: boolean;
	isOver: boolean;
	isHoveringOverSameRow: boolean;
	hovered: boolean;
	disabled: boolean;
	readOnly: boolean;
}

const useStyles = createStyles(
	(
		theme,
		{
			isFirstIndicator,
			isLastIndicator,
			isOver,
			isHoveringOverSameRow,
			hovered,
			disabled,
			readOnly,
		}: DnDVerticalIndicatorStylesProps
	) => {
		let width = ITEM_GAP;
		let paddingLeft = `calc((${ITEM_GAP}px - ${INDICATOR_SIZE}px) / 2)`;
		let paddingRight = `calc((${ITEM_GAP}px - ${INDICATOR_SIZE}px) / 2)`;
		if (isFirstIndicator) {
			paddingLeft = `0`;
			width -= ITEM_GAP / 2;
		}
		if (isLastIndicator) {
			paddingRight = `0`;
			width -= ITEM_GAP / 2;
		}

		const isVisible =
			!readOnly &&
			((isOver && (!disabled || isHoveringOverSameRow)) || hovered);

		let backgroundColor: string;
		if (disabled) {
			backgroundColor = theme.other.getColor('border/primary/disabled');
		} else if (isOver) {
			backgroundColor = theme.other.getColor('border/emphasis/default');
		} else {
			backgroundColor = theme.other.getColor('border/tertiary/default');
		}

		return {
			wrapper: {
				width,
				padding: `0 ${paddingRight} 0 ${paddingLeft}`,
				'&:hover': {
					cursor: disabled || readOnly ? 'default' : 'col-resize',
				},
			},
			indicator: {
				width: INDICATOR_SIZE,
				height: '100%',
				backgroundColor,
				borderRadius: theme.radius.md,
				visibility: isVisible ? 'visible' : 'hidden',
			},
		};
	}
);

export function DnDVerticalIndicator({
	id,
	isFirstItem,
	isLastItem,
	isHoveringOverSameRow,
	disabled,
	readOnly = false,
	onResizeStart,
}: DnDVerticalIndicatorProps) {
	const { setNodeRef, over } = useDroppable({
		id,
		disabled: readOnly,
	});

	const { ref: hoverRef, hovered } = useHover();

	const ref = useMergedRef(setNodeRef, hoverRef);

	const isFirstIndicator =
		isFirstItem && id.includes(VERTICAL_INDICATOR_BEFORE_ID_PREFIX);
	const isLastIndicator =
		isLastItem && id.includes(VERTICAL_INDICATOR_AFTER_ID_PREFIX);

	const handleMouseDown = (event: React.MouseEvent) => {
		if (disabled || readOnly) return;
		event.preventDefault();
		event.stopPropagation();
		if (onResizeStart) {
			onResizeStart(id, event.clientX);
		}
	};

	const { classes } = useStyles({
		isFirstIndicator,
		isLastIndicator,
		isOver: readOnly ? false : id === over?.id,
		isHoveringOverSameRow,
		hovered: readOnly ? false : hovered,
		disabled,
		readOnly,
	});

	return (
		<Box ref={ref} className={classes.wrapper} onMouseDown={handleMouseDown}>
			<Box className={classes.indicator} />
		</Box>
	);
}
